import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { perfumeAPI, transitOrdersAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import {
  ArrowLeft,
  Search,
  FileText,
  Trash2,
  Plus,
  Minus,
  Download,
} from "lucide-react";

const TransitOut = () => {
  const navigate = useNavigate();
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [orderItems, setOrderItems] = useState({});
  const searchRef = useRef(null);

  useEffect(() => {
    loadPerfumes();
  }, []);

  const loadPerfumes = async () => {
    try {
      const res = await perfumeAPI.getAll();
      setPerfumes(res.data);
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = (perfumeId, delta) => {
    setOrderItems((prev) => {
      const current = prev[perfumeId] || 0;
      const newVal = Math.max(0, current + delta);
      if (newVal === 0) {
        const { [perfumeId]: removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [perfumeId]: newVal };
    });
  };

  const setQuantity = (perfumeId, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue <= 0) {
      const { [perfumeId]: removed, ...rest } = orderItems;
      setOrderItems(rest);
    } else {
      setOrderItems({ ...orderItems, [perfumeId]: numValue });
    }
  };

  const removeItem = (perfumeId) => {
    const { [perfumeId]: removed, ...rest } = orderItems;
    setOrderItems(rest);
  };

  const generatePDF = async () => {
    const items = Object.entries(orderItems);
    if (items.length === 0) {
      toast.error("Aggiungi almeno un profumo all'ordine");
      return;
    }

    // Save order to history
    try {
      const orderItemsList = items.map(([perfumeId, liters]) => {
        const perfume = perfumes.find((p) => p.id === perfumeId);
        return {
          perfume_id: perfumeId,
          perfume_code: perfume?.code || "",
          perfume_name: perfume?.name || "",
          quantity_liters: liters,
        };
      });
      await transitOrdersAPI.create({
        order_type: "out",
        items: orderItemsList,
      });
    } catch (error) {
      console.error("Error saving order:", error);
    }

    const doc = new jsPDF();
    const date = new Date().toLocaleDateString("it-IT");
    const time = new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

    // Header
    doc.setFontSize(24);
    doc.setFont("helvetica", "bold");
    doc.text("EQUIVALENCIA EQUIPE", 105, 25, { align: "center" });
    
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text("Ordine in Uscita", 105, 35, { align: "center" });
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Data: ${date} - Ore: ${time}`, 105, 45, { align: "center" });
    
    // Divider
    doc.setDrawColor(201, 162, 39);
    doc.line(20, 55, 190, 55);

    // Table header
    doc.setTextColor(0);
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("CODICE", 20, 65);
    doc.text("NOME PROFUMO", 50, 65);
    doc.text("LITRI", 170, 65, { align: "right" });

    // Table content
    doc.setFont("helvetica", "normal");
    let y = 75;
    let totalLiters = 0;

    items.forEach(([perfumeId, liters]) => {
      const perfume = perfumes.find((p) => p.id === perfumeId);
      if (perfume) {
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
        doc.text(perfume.code, 20, y);
        doc.text(perfume.name.substring(0, 50), 50, y);
        doc.text(`${liters.toFixed(1)} L`, 170, y, { align: "right" });
        totalLiters += liters;
        y += 8;
      }
    });

    // Total
    y += 10;
    doc.setDrawColor(201, 162, 39);
    doc.line(20, y, 190, y);
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("TOTALE LITRI:", 130, y);
    doc.text(`${totalLiters.toFixed(1)} L`, 170, y, { align: "right" });

    // Footer
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(150);
    doc.text("Documento generato automaticamente da Equivalencia Equipe", 105, 285, { align: "center" });

    // Save
    doc.save(`ordine_uscita_${date.replace(/\//g, "-")}.pdf`);
    toast.success("PDF generato con successo!");
  };

  // Smart search: exact match first, then starts with, then contains
  const filteredPerfumes = perfumes
    .filter(
      (p) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.code.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const query = searchQuery.toLowerCase();
      const aCode = a.code.toLowerCase();
      const bCode = b.code.toLowerCase();
      
      // Exact match first
      if (aCode === query && bCode !== query) return -1;
      if (bCode === query && aCode !== query) return 1;
      
      // Then starts with
      const aStarts = aCode.startsWith(query);
      const bStarts = bCode.startsWith(query);
      if (aStarts && !bStarts) return -1;
      if (bStarts && !aStarts) return 1;
      
      // Sort by code length (shorter = more relevant)
      if (aStarts && bStarts) {
        return aCode.length - bCode.length;
      }
      
      return 0;
    });

  const orderCount = Object.keys(orderItems).length;

  return (
    <div data-testid="transit-out-page" className="animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/transito")}
            className="p-2.5 text-gray-500 hover:text-[#C9A227] bg-white rounded-xl shadow-sm hover:shadow transition-all"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div>
            <h1 className="font-serif text-3xl text-gray-900">Ordine in Uscita</h1>
            <p className="text-gray-500 text-sm mt-1">
              Seleziona i profumi da ordinare
            </p>
          </div>
        </div>
        <Button
          onClick={generatePDF}
          data-testid="generate-pdf-btn"
          disabled={orderCount === 0}
          className="bg-[#C9A227] text-white hover:bg-[#D4AF37] rounded-xl px-6 h-12 text-sm font-semibold shadow-md hover:shadow-lg disabled:opacity-50 transition-all"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={2} />
          Genera PDF ({orderCount})
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-8 max-w-xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" strokeWidth={1.5} />
        <Input
          ref={searchRef}
          data-testid="search-transit-out"
          placeholder="Cerca per nome o codice..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-12 bg-white border-gray-200 rounded-xl h-12 focus:border-[#C9A227] text-gray-900 placeholder:text-gray-400 shadow-sm"
        />
      </div>

      {/* Perfume Grid */}
      {loading ? (
        <div className="text-center py-20 text-gray-500">Caricamento...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredPerfumes.map((perfume) => {
            const quantity = orderItems[perfume.id] || 0;
            const isSelected = quantity > 0;

            return (
              <div
                key={perfume.id}
                className={`perfume-transit-card p-5 ${isSelected ? "selected" : ""}`}
                data-testid={`perfume-card-${perfume.code}`}
              >
                <div className="mb-4">
                  <p className="text-[#C9A227] text-xs font-semibold tracking-wider mb-1">{perfume.code}</p>
                  <h3 className="text-gray-900 font-bold text-sm leading-tight line-clamp-2 h-10">
                    {perfume.name}
                  </h3>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => updateQuantity(perfume.id, -1)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    data-testid={`decrease-${perfume.code}`}
                  >
                    <Minus className="w-4 h-4 text-gray-600" strokeWidth={2} />
                  </button>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    value={quantity || ""}
                    onChange={(e) => setQuantity(perfume.id, e.target.value)}
                    placeholder="0"
                    className="w-20 h-10 text-center bg-white border-gray-200 rounded-lg text-gray-900 font-semibold"
                    data-testid={`quantity-${perfume.code}`}
                  />
                  <button
                    onClick={() => updateQuantity(perfume.id, 1)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    data-testid={`increase-${perfume.code}`}
                  >
                    <Plus className="w-4 h-4 text-gray-600" strokeWidth={2} />
                  </button>
                  <span className="text-gray-500 text-sm font-medium ml-1">L</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Order Summary */}
      {orderCount > 0 && (
        <div className="fixed bottom-6 right-6 left-6 md:left-auto md:w-96">
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-500 font-medium">Riepilogo Ordine</span>
              <span className="text-[#C9A227] text-sm font-bold">{orderCount} profumi</span>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-2 mb-4">
              {Object.entries(orderItems).map(([id, qty]) => {
                const p = perfumes.find((x) => x.id === id);
                return p ? (
                  <div key={id} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 truncate max-w-[200px] font-medium">{p.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 font-bold">{qty} L</span>
                      <button
                        onClick={() => removeItem(id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : null;
              })}
            </div>
            <Button
              onClick={generatePDF}
              className="w-full bg-[#C9A227] text-white hover:bg-[#D4AF37] rounded-xl h-11 text-sm font-semibold shadow-md"
            >
              <FileText className="w-4 h-4 mr-2" />
              Genera PDF
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransitOut;
